import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { useNavigationStore } from '@/store/navigation.store';
import { useGlobalDateRange } from '@/store/date-filter.store';
import { ACTIONABLE_STATUSES } from '@/constants/status-filters';

/**
 * Direct API-based prepayments count hook - aligned with compliance pattern
 * Uses global date filter and actionable status filters for accurate counts
 * Includes AbortController for proper lifecycle management
 */
export function usePrepaymentsCountDirect() {
  const [count, setCount] = useState(0);
  const [hasData, setHasData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { selectedClientId, selectedEntityId } = useNavigationStore();
  const { dateRange } = useGlobalDateRange();

  // Track current context to prevent stale updates
  const currentContextRef = useRef({ clientId: selectedClientId, entityId: selectedEntityId });
  // Track abort controller
  const abortControllerRef = useRef<AbortController | null>(null);

  const loadCount = useCallback(async () => {
    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!selectedClientId || !selectedEntityId) {
      setCount(0);
      setHasData(false);
      return;
    }

    // Update context ref
    currentContextRef.current = { clientId: selectedClientId, entityId: selectedEntityId };

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    try {
      const response = await api.getDashboardTransactions({
        client_id: selectedClientId,
        entity_id: selectedEntityId === 'all' ? undefined : selectedEntityId,
        transaction_type: 'ACCPAY',
        status_filters: [...ACTIONABLE_STATUSES],
        date_from: dateRange?.start,
        date_to: dateRange?.end,
        page: 1,
        limit: 1,
      });

      // Check if context changed during request
      if (controller.signal.aborted ||
          currentContextRef.current.clientId !== selectedClientId ||
          currentContextRef.current.entityId !== selectedEntityId) {
        return;
      }

      const totalCount = response?.total || 0;
      setCount(totalCount);
      setHasData(true);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('[usePrepaymentsCountDirect] Failed to load count:', error);
      setCount(0);
      setHasData(false);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [selectedClientId, selectedEntityId, dateRange]);

  useEffect(() => {
    loadCount();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadCount]);

  return {
    currentCount: count,
    hasData,
    isLoading,
    reload: loadCount
  };
}